import { useEffect, useState } from "react";
import { ChevronDown, Coins } from "lucide-react";
import { PRICING_KEYS, PRICING_LABELS, formatUsd } from "./voiceCost";

/**
 * Temporaneo (fase di test): costo live della chat vocale.
 * Si disattiva con «Mostra costo all'utente» in Admin → Intelligenza artificiale → Chat vocale.
 */
export default function VoiceCostMeter({ getLiveCost, model, compact = false }) {
  const [snapshot, setSnapshot] = useState(() => getLiveCost());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setSnapshot(getLiveCost()), 250);
    return () => clearInterval(timer);
  }, [getLiveCost]);

  if (compact) {
    return (
      <span className="tabular-nums text-xs text-white/80" data-testid="voice-cost-compact">
        {formatUsd(snapshot.total)}
      </span>
    );
  }

  return (
    <div className="relative" data-testid="voice-cost-meter">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs text-white/90 backdrop-blur-md hover:bg-white/15 transition-colors"
        aria-expanded={open}
      >
        <Coins className="w-3.5 h-3.5 text-amber-300" />
        <span>Costo sessione</span>
        <span className="font-semibold tabular-nums">{formatUsd(snapshot.total)}</span>
        {snapshot.estimating && <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" title="Stima in corso" />}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-[300px] rounded-2xl border border-white/15 bg-slate-900/95 p-3 text-xs text-white shadow-2xl backdrop-blur-md z-10">
          <div className="flex items-center justify-between pb-2 mb-2 border-b border-white/10">
            <span className="text-white/60">Modello</span>
            <span className="font-medium">{model || "—"}</span>
          </div>
          <table className="w-full">
            <thead>
              <tr className="text-white/50">
                <th className="text-left font-normal pb-1">Voce</th>
                <th className="text-right font-normal pb-1">Token</th>
                <th className="text-right font-normal pb-1">Costo</th>
              </tr>
            </thead>
            <tbody>
              {PRICING_KEYS.map((key) => (
                <tr key={key}>
                  <td className="py-0.5 text-white/80">{PRICING_LABELS[key]}</td>
                  <td className="py-0.5 text-right tabular-nums text-white/70">
                    {(snapshot.usage?.[key] || 0).toLocaleString("it-IT")}
                  </td>
                  <td className="py-0.5 text-right tabular-nums">{formatUsd(snapshot.breakdown?.[key] || 0, 5)}</td>
                </tr>
              ))}
              <tr className="border-t border-white/10">
                <td className="pt-1.5 font-semibold" colSpan={2}>Totale</td>
                <td className="pt-1.5 text-right font-semibold tabular-nums">{formatUsd(snapshot.total)}</td>
              </tr>
            </tbody>
          </table>
          <p className="mt-2 text-[10px] leading-snug text-white/45">
            Visibile solo in fase di test. Stima in tempo reale durante il parlato, riallineata ai consumi ufficiali
            OpenAI a fine di ogni risposta.
          </p>
        </div>
      )}
    </div>
  );
}
